import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import "./ade-outreach.css";
import { api } from "./api";
import { VIEWS, todayISO } from "./constants";
import Header from "./Header";
import KpiStrip from "./KpiStrip";
import FilterBar from "./FilterBar";
import ContactsTable from "./ContactsTable";
import ContactDrawer from "./ContactDrawer";
import AddContactDialog from "./AddContactDialog";
import ActionsView from "./ActionsView";
import TemplatesView from "./TemplatesView";
import AssetsView from "./AssetsView";
import PlaybookView from "./PlaybookView";
import RemindersStrip from "./RemindersStrip";
import GmailPanel from "./GmailPanel";

const ORDER = { High: 0, Medium: 1, Low: 2 };
const EMPTY_FILTERS = { q: "", priority: "", status: "", reply: "", sort: "priority" };

export default function AdeOutreachPage() {
  const [view, setView] = useState("labels");
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [actions, setActions] = useState([]);
  const [playbook, setPlaybook] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [scoring, setScoring] = useState(null);
  const [reminders, setReminders] = useState(null);

  const refreshStats = useCallback(() => { api.stats().then(setStats); api.reminders().then(setReminders); }, []);
  const reloadContacts = useCallback(() => { api.contacts().then(setContacts); refreshStats(); }, [refreshStats]);

  useEffect(() => {
    Promise.all([api.contacts(), api.templates(), api.assets(), api.actions(), api.playbook(), api.stats(), api.tracks(), api.scoring(), api.reminders()])
      .then(([c, t, a, ac, p, s, tr, sc, rem]) => { setContacts(c); setTemplates(t); setAssets(a); setActions(ac); setPlaybook(p); setStats(s); setTracks(tr); setScoring(sc); setReminders(rem); })
      .catch(() => toast.error("Could not load ADE outreach data"));
  }, []);

  const visible = useMemo(() => {
    const q = filters.q.toLowerCase();
    return contacts
      .filter((c) => c.tab === view)
      .filter((c) => !filters.priority || c.priority === filters.priority)
      .filter((c) => !filters.status || c.outreachStatus === filters.status)
      .filter((c) => !filters.reply || c.reply === filters.reply)
      .filter((c) => !q || [c.targetName, c.company, c.role, c.cityNotes, c.soundFitNotes, c.email].join(" ").toLowerCase().includes(q))
      .sort((a, b) => (filters.sort === "score" ? (b.replyScore || 0) - (a.replyScore || 0) : ORDER[a.priority] - ORDER[b.priority] || (b.replyScore || 0) - (a.replyScore || 0)));
  }, [contacts, view, filters]);

  const patch = async (c, data, msg) => {
    const updated = await api.updateContact(c.id, data);
    setContacts((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    if (selected?.id === c.id) setSelected(updated);
    refreshStats();
    if (msg) toast.success(msg);
    return updated;
  };

  const onStatus = (c, v) => {
    const extra = {};
    if (v === "Sent" && !c.sentDate) extra.sentDate = todayISO();
    if (v === "Replied" || v === "Meeting Booked") { extra.reply = "Yes"; extra.followUpNeeded = "Yes"; }
    if (v === "No Response") extra.reply = "No";
    patch(c, { outreachStatus: v, ...extra }, `${c.targetName}: ${v}`);
  };
  const onNudgeSent = (c) => patch(c, { nudgeSentDate: todayISO() }, "Nudge logged");
  const onFollowUpSent = (c) => patch(c, { followUpSent: "Yes" }, "Follow-up logged");

  const onSave = async (form) => { await patch(form, form, "Saved"); setSelected(null); };
  const onDelete = async (c) => {
    await api.deleteContact(c.id);
    setContacts((prev) => prev.filter((x) => x.id !== c.id));
    setSelected(null); refreshStats(); toast.success("Removed");
  };
  const onCreate = async (form) => {
    const created = await api.createContact(form);
    setContacts((prev) => [...prev, created]);
    setAdding(false); setView(form.tab); refreshStats(); toast.success("Target added");
  };
  const onToggleAction = async (a, done) => {
    const u = await api.updateAction(a.id, done);
    setActions((prev) => prev.map((x) => (x.id === a.id ? u : x)));
  };
  const onUpdateAsset = async (a, status) => {
    const u = await api.updateAsset(a.id, { status });
    setAssets((prev) => prev.map((x) => (x.id === a.id ? u : x)));
  };
  const onReset = async () => {
    if (!window.confirm("Reset all contacts, assets and actions to the curated starting list? Your edits will be lost.")) return;
    await api.reset();
    const [c, a, ac] = await Promise.all([api.contacts(), api.assets(), api.actions()]);
    setContacts(c); setAssets(a); setActions(ac); refreshStats(); toast.success("Reset to starting list");
  };
  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await api.exportXlsx();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "ADE_2026_Outreach_Alchemist_CA.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel workbook downloaded");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const isContacts = view === "labels" || view === "management";

  return (
    <div className="ade-root -mx-4 -my-6 sm:-mx-6 sm:-my-8 md:-m-12" data-testid="ade-outreach-page">
      <Header onExport={onExport} onAdd={() => setAdding(true)} onReset={onReset} exporting={exporting} />
      <main className="max-w-[1600px] mx-auto px-5 md:px-8 py-6 space-y-6">
        <KpiStrip stats={stats} />
        <RemindersStrip data={reminders} contacts={contacts} onOpen={setSelected} onNudgeSent={onNudgeSent} onFollowUpSent={onFollowUpSent} />
        <GmailPanel onContactsChanged={reloadContacts} onOpenContact={(id) => { const c = contacts.find((x) => x.id === id); if (c) setSelected(c); }} />
        <nav className="flex flex-wrap gap-1 p-1 rounded-full w-fit" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="view-tabs">
          {VIEWS.map((v) => (
            <button key={v.id} className={`tab ${view === v.id ? "tab-active" : ""}`} onClick={() => setView(v.id)} data-testid={`tab-${v.id}-trigger`}>{v.label}</button>
          ))}
        </nav>

        {isContacts && (
          <div className="space-y-4 rise">
            <FilterBar filters={filters} setFilters={setFilters} count={visible.length} />
            <ContactsTable contacts={visible} onOpen={setSelected} onStatus={onStatus} />
          </div>
        )}
        {(view === "pre" || view === "post") && <ActionsView lane={view} actions={actions} contacts={contacts} onToggle={onToggleAction} onOpen={setSelected} />}
        {view === "templates" && <TemplatesView templates={templates} />}
        {view === "assets" && <AssetsView assets={assets} onUpdate={onUpdateAsset} />}
        {view === "playbook" && <PlaybookView data={playbook} />}
      </main>

      {selected && <ContactDrawer contact={selected} onClose={() => setSelected(null)} onSave={onSave} onDelete={onDelete} tracks={tracks} scoring={scoring} />}
      <AddContactDialog open={adding} defaultTab={isContacts ? view : "labels"} onClose={() => setAdding(false)} onCreate={onCreate} />
    </div>
  );
}
